'use client';

import React, { useMemo, useState } from 'react';
import isSvg from 'is-svg';
import { Copy } from 'lucide-react';
import { css } from 'styled-system/css';
import { scrollbar } from 'styled-system/recipes';

import Copiable from '@shaojiang/components/Copiable';
import { Alert } from '@shaojiang/components/ui/alert';
import { Button } from '@shaojiang/components/ui/button';

const imageUri = (base64: string) => `data:image/svg+xml;base64,${base64}`;

/** UTF-8 safe base64 (window.btoa alone throws on non-Latin1 characters). */
const toBase64 = (text: string) => {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return window.btoa(binary);
};

const checkerboard = css({
  bgImage:
    "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==')",
});

const textareaStyle = css({
  p: '5',
  mb: '5',
  w: 'full',
  fontFamily: 'mono',
  outline: 'none',
  boxShadow: 'lg',
  borderWidth: '1px',
  borderColor: 'border.brand',
  resize: 'none',
  color: 'fg.default',
  bg: 'transparent',
  _focusVisible: { borderColor: 'primary.9' },
});

export default function Svg2Base64() {
  const [svg, setSvg] = useState('');
  const [base64, setBase64] = useState('');
  const [size, setSize] = useState({ width: 100, height: 100 });

  const isValidSvg = useMemo(() => isSvg(svg), [svg]);
  // Preview through <img> rather than injecting markup, so pasted SVGs can't run scripts
  const preview = useMemo(
    () => (svg && isValidSvg ? imageUri(toBase64(svg)) : ''),
    [svg, isValidSvg],
  );

  const reset = () => {
    setSvg('');
    setBase64('');
  };

  return (
    <div>
      <h4 className={css({ my: '4' })}>Inline SVG</h4>

      <textarea
        autoFocus
        name='svg'
        rows={10}
        value={svg}
        aria-label='SVG markup'
        className={`${scrollbar()} ${textareaStyle}`}
        onChange={(e) => {
          setSvg(e.target.value.trim().replace(/\r\n/g, ''));
          setBase64('');
        }}
      />
      {preview && (
        <div className={`${checkerboard} ${css({ display: 'inline-flex', mb: '6' })}`}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={preview}
            alt='SVG preview'
            className={css({ maxH: '80', maxW: 'full' })}
            onLoad={(e) =>
              setSize({ width: e.currentTarget.clientWidth, height: e.currentTarget.clientHeight })
            }
          />
        </div>
      )}
      {svg && !isValidSvg && (
        <Alert.Root mb='5' borderColor='error.main'>
          <Alert.Content>
            <Alert.Title color='error.main'>Please input a valid SVG</Alert.Title>
          </Alert.Content>
        </Alert.Root>
      )}

      <div className={css({ display: 'flex', flexWrap: 'wrap', gap: '4' })}>
        <Button onClick={reset} size='lg' variant='outline' colorPalette='gray'>
          Clear
        </Button>

        {isValidSvg && (
          <Button onClick={() => setBase64(toBase64(svg))} size='lg'>
            Convert
          </Button>
        )}

        {base64 && isValidSvg && (
          <>
            <Copiable tipPlacement='top' text={imageUri(base64)}>
              <Button size='lg'>
                <Copy />
                Base64
              </Button>
            </Copiable>
            <Copiable
              tipPlacement='top'
              text={`background-image: url(${imageUri(base64)});
background-repeat: no-repeat;
width: ${size.width}px;
height: ${size.height}px;`}
            >
              <Button size='lg'>
                <Copy />
                CSS Bg with Base64
              </Button>
            </Copiable>
          </>
        )}
      </div>

      {base64 && isValidSvg && (
        <>
          <h4 className={css({ my: '6' })}>Base64</h4>
          <textarea
            name='base64'
            rows={10}
            value={imageUri(base64)}
            aria-label='Base64 data URI'
            className={`${scrollbar()} ${textareaStyle}`}
            readOnly
          />
          <div
            className={`${checkerboard} ${css({ display: 'inline-flex', maxH: '80', maxW: '80' })}`}
          >
            <i
              style={{
                display: 'flex',
                backgroundImage: `url(${imageUri(base64)})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                width: size.width,
                height: size.height,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
